const DEV_BROWSER_ENABLED_COOKIE = "cc_debug_browser";
const DEV_BROWSER_USER_ID_COOKIE = "cc_debug_user_id";
const DEV_BROWSER_ENABLED_STORAGE_KEY = "citychronicle.dev.browser.enabled";
const DEV_BROWSER_USER_ID_STORAGE_KEY = "citychronicle.dev.browser.userId";
const DEFAULT_DEV_USER_ID = "900001";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

export interface DevBrowserModeState {
  enabled: boolean;
  userId: string;
  source: "query" | "cookie" | "storage" | "disabled";
}

let cachedState: DevBrowserModeState | null = null;

export function configureDevBrowserModeFromUrl(): DevBrowserModeState {
  const params = new URLSearchParams(window.location.search);
  const disableRequested = hasAny(params, [
    "cc_debug_off",
    "ccDebugOff",
    "devAuthOff",
  ]);

  if (disableRequested) {
    clearDevBrowserMode();
    cachedState = { enabled: false, userId: DEFAULT_DEV_USER_ID, source: "disabled" };
    stripDebugParamsFromUrl(params);
    applyDebugDocumentState(cachedState);
    return cachedState;
  }

  const enableParam = firstValue(params, [
    "cc_debug_browser",
    "ccDebugBrowser",
    "cc_dev_browser",
    "devBrowser",
  ]);
  const userIdParam = firstValue(params, [
    "cc_debug_user_id",
    "ccDevUserId",
    "cc_dev_user_id",
    "devTelegramUserId",
    "devUserId",
  ]);
  const enabledByQuery = normalizeBoolean(enableParam) || Boolean(userIdParam);

  if (enabledByQuery) {
    const userId = sanitizeUserId(userIdParam) ?? readDevBrowserUserId() ?? DEFAULT_DEV_USER_ID;
    persistDevBrowserMode(userId);
    cachedState = { enabled: true, userId, source: "query" };
    stripDebugParamsFromUrl(params);
    applyDebugDocumentState(cachedState);
    logDebugMode(cachedState);
    return cachedState;
  }

  cachedState = readDevBrowserMode();
  applyDebugDocumentState(cachedState);
  if (cachedState.enabled) logDebugMode(cachedState);
  return cachedState;
}

export function isDevBrowserModeEnabled(): boolean {
  return currentDevBrowserMode().enabled;
}

export function devBrowserAuthUserId(): string | null {
  const state = currentDevBrowserMode();
  return state.enabled ? state.userId : null;
}

export function currentDevBrowserMode(): DevBrowserModeState {
  if (cachedState) return cachedState;
  cachedState = readDevBrowserMode();
  applyDebugDocumentState(cachedState);
  return cachedState;
}

function readDevBrowserMode(): DevBrowserModeState {
  const cookieEnabled = readCookie(DEV_BROWSER_ENABLED_COOKIE) === "1";
  const storageEnabled = safeLocalStorageGet(DEV_BROWSER_ENABLED_STORAGE_KEY) === "1";
  const enabled = cookieEnabled || storageEnabled;
  const userId = readDevBrowserUserId() ?? DEFAULT_DEV_USER_ID;
  return {
    enabled,
    userId,
    source: cookieEnabled ? "cookie" : storageEnabled ? "storage" : "disabled",
  };
}

function readDevBrowserUserId(): string | null {
  return sanitizeUserId(readCookie(DEV_BROWSER_USER_ID_COOKIE))
    ?? sanitizeUserId(safeLocalStorageGet(DEV_BROWSER_USER_ID_STORAGE_KEY));
}

function persistDevBrowserMode(userId: string): void {
  writeCookie(DEV_BROWSER_ENABLED_COOKIE, "1");
  writeCookie(DEV_BROWSER_USER_ID_COOKIE, userId);
  safeLocalStorageSet(DEV_BROWSER_ENABLED_STORAGE_KEY, "1");
  safeLocalStorageSet(DEV_BROWSER_USER_ID_STORAGE_KEY, userId);
}

function clearDevBrowserMode(): void {
  deleteCookie(DEV_BROWSER_ENABLED_COOKIE);
  deleteCookie(DEV_BROWSER_USER_ID_COOKIE);
  safeLocalStorageRemove(DEV_BROWSER_ENABLED_STORAGE_KEY);
  safeLocalStorageRemove(DEV_BROWSER_USER_ID_STORAGE_KEY);
}

function writeCookie(name: string, value: string): void {
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
}

function deleteCookie(name: string): void {
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  const raw = document.cookie
    .split(";")
    .map((item) => item.trim())
    .find((item) => item.startsWith(prefix))
    ?.slice(prefix.length);
  return raw === undefined ? null : decodeURIComponentSafe(raw);
}

function decodeURIComponentSafe(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function safeLocalStorageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Browser debug mode must still work with cookies when localStorage is blocked.
  }
}

function safeLocalStorageRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage restrictions.
  }
}

function hasAny(params: URLSearchParams, names: string[]): boolean {
  return names.some((name) => params.has(name));
}

function firstValue(params: URLSearchParams, names: string[]): string | null {
  for (const name of names) {
    const value = params.get(name);
    if (value !== null) return value;
  }
  return null;
}

function normalizeBoolean(value: string | null): boolean {
  if (value === null) return false;
  const normalized = value.trim().toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "yes" || normalized === "on";
}

function sanitizeUserId(value: string | null | undefined): string | null {
  const normalized = value?.trim();
  if (!normalized || !/^\d{3,18}$/.test(normalized)) return null;
  return normalized;
}

function stripDebugParamsFromUrl(params: URLSearchParams): void {
  [
    "cc_debug_browser",
    "ccDebugBrowser",
    "cc_dev_browser",
    "devBrowser",
    "cc_debug_user_id",
    "ccDevUserId",
    "cc_dev_user_id",
    "devTelegramUserId",
    "devUserId",
    "cc_debug_off",
    "ccDebugOff",
    "devAuthOff",
  ].forEach((name) => params.delete(name));

  const query = params.toString();
  const nextUrl = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
  window.history.replaceState({}, "", nextUrl);
}

function applyDebugDocumentState(state: DevBrowserModeState): void {
  document.documentElement.toggleAttribute("data-cc-debug-browser", state.enabled);
  document.documentElement.dataset.ccDebugUserId = state.enabled ? state.userId : "";
}

function logDebugMode(state: DevBrowserModeState): void {
  console.info(
    `[City Chronicle] Browser DevTools mode enabled via ${state.source}. `
    + `Using X-Dev-Telegram-User-Id=${state.userId}. `
    + `Backend must have APP_DEV_AUTH_ENABLED=true.`
  );
}
